import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { type Observable, combineLatest, filter, map, startWith } from "rxjs";

import { type GetOrganicStatsQuery } from "../../api/queries";
import { type DimensionFunnelMap, createDefaultDimensionsFunnelMap } from "../../models/dimension-funnel";

import { mapDimensionBreakdownFunnelData } from "./mapDimensionBreakdownFunnelData";

export interface DimensionBreakdownFunnelInput {
    dimensionBreakdownData: GetOrganicStatsQuery | undefined;
    dimensionBreakdownLoading: boolean;
    dimensionBreakdownTotalData: GetOrganicStatsQuery | undefined;
    dimensionBreakdownTotalLoading: boolean;
}

export interface DimensionBreakdownFunnelOutput {
    dimensionFunnelMap: DimensionFunnelMap;
    isEmpty: boolean;
}

const defaults = {
    dimensionFunnelMap: createDefaultDimensionsFunnelMap(),
    isEmpty: true,
};

const renderDimensionBreakdownFunnelEffect = (
    data$: Observable<GetOrganicStatsQuery | undefined>,
    loading$: Observable<boolean>,
    dataTotal$: Observable<GetOrganicStatsQuery | undefined>,
    loadingTotal$: Observable<boolean>,
): Observable<ComponentEffect<DimensionBreakdownFunnelOutput>> => {
    return combineLatest([data$, dataTotal$, loading$, loadingTotal$]).pipe(
        filter((values): values is [GetOrganicStatsQuery, GetOrganicStatsQuery, boolean, boolean] => {
            const [data, dataTotal, loading, loadingTotal] = values;
            return !loading && !loadingTotal && data !== undefined && dataTotal !== undefined;
        }),
        map(([data, dataTotal]) => mapDimensionBreakdownFunnelData(data, dataTotal)),
        startWith(defaults),
        map((metrics) => toRender<DimensionBreakdownFunnelOutput>(metrics)),
    );
};

export const dimensionBreakdownFunnelEffect: Aperture<
    DimensionBreakdownFunnelInput,
    ComponentEffect<DimensionBreakdownFunnelOutput>
> = ({ observe }) => {
    const dimensionBreakdownData$ = observe<GetOrganicStatsQuery>("dimensionBreakdownData");
    const dimensionBreakdownLoading$ = observe<boolean>("dimensionBreakdownLoading");
    const dimensionBreakdownTotalData$ = observe<GetOrganicStatsQuery>("dimensionBreakdownTotalData");
    const dimensionBreakdownTotalLoading$ = observe<boolean>("dimensionBreakdownTotalLoading");

    return renderDimensionBreakdownFunnelEffect(
        dimensionBreakdownData$,
        dimensionBreakdownLoading$,
        dimensionBreakdownTotalData$,
        dimensionBreakdownTotalLoading$,
    );
};
